import $ from "jquery";
import _ from "lodash";
import moment from "moment-timezone";
import { Datepicker } from "vanillajs-datepicker";
import { modal, modalContentSelector } from "../modal";
import { cloneableFactory } from "../cloneable";
import { EventEmitter } from "../../events/eventEmitter";
class DatePicker extends EventEmitter {
    constructor(picker) {
        var _a;
        super();
        this.textInput = picker.find("[data-date-picker-input]");
        this.format = picker.data("date-picker-format") || "DD/MM/YYYY";
        this.displayFormat = picker.data("date-picker-display-format") || this.format;
        this.fromInput = picker.find("[data-date-picker-from]");
        this.toInput = picker.find("[data-date-picker-to]");
        this.singleDatePicker = !_.some(this.toInput);
        this.pickerLevel = picker.data("date-picker-level") || "days";
        this.showTime = picker.is("[data-date-picker-show-time]");
        this.updateTextInput();
        const values = this.getValue();
        const container = picker.closest(modalContentSelector);
        switch (this.pickerLevel) {
            case "days":
                const showRanges = picker.is("[data-date-picker-show-ranges]");
                let ranges;
                if (showRanges) {
                    const allFrom = (picker.data("date-picker-all-from"));
                    const ytdFrom = (picker.data("date-picker-ytd-from"));
                    ranges = Object.assign(Object.assign(Object.assign({}, (allFrom
                        ? {
                            All: [
                                moment(allFrom, this.format).toDate(),
                                moment().toDate(),
                            ],
                        }
                        : {})), { Today: [moment().toDate(), moment().toDate()], Yesterday: [
                            moment().subtract(1, "days").toDate(),
                            moment().subtract(1, "days").toDate(),
                        ], "Last 7 Days": [
                            moment().subtract(6, "days").toDate(),
                            moment().toDate(),
                        ], "This Month": [
                            moment().startOf("month").toDate(),
                            moment().toDate(),
                        ], "Last Month": [
                            moment().subtract(1, "month").startOf("month").toDate(),
                            moment().subtract(1, "month").endOf("month").toDate(),
                        ], "Last 3 Months": [
                            moment().subtract(3, "month").toDate(),
                            moment().toDate(),
                        ] }), (ytdFrom
                        ? {
                            "Year to date": [
                                moment(ytdFrom, this.format).toDate(),
                                moment().toDate(),
                            ],
                        }
                        : {}));
                }
                this.textInput.daterangepicker({
                    locale: {
                        format: this.displayFormat,
                    },
                    startDate: values.from,
                    endDate: values.to,
                    singleDatePicker: this.singleDatePicker,
                    showDropdowns: false,
                    timePicker: this.showTime,
                    timePicker24Hour: true,
                    timePickerIncrement: 15,
                    linkedCalendars: true,
                    opens: "right",
                    showCustomRangeLabel: false,
                    alwaysShowCalendars: true,
                    applyButtonClasses: "btn-primary",
                    cancelButtonClasses: "btn-secondary",
                    buttonClasses: [""],
                    parentEl: container[0],
                    autoUpdateInput: false,
                    autoApply: this.singleDatePicker,
                    ranges,
                }, (from, to) => this.updateInputs(from, to));
                if (!ranges)
                    (_a = this.textInput
                        .data("daterangepicker")) === null || _a === void 0 ? void 0 : _a.container.find(".ranges").remove();
                picker.on("timerender.daterangepicker daterender.daterangepicker", (_e, container) => this.emit("rendered", container[0]));
                break;
            case "months":
                const monthPicker = new Datepicker(this.textInput[0], {
                    autohide: true,
                    pickLevel: 1,
                    container: container[0],
                    format: {
                        //@ts-ignore - return type for toValue is incorrectly specified
                        toValue: (date) => moment(date, this.format).toDate(),
                        toDisplay: (date) => moment(date).format(this.displayFormat),
                    },
                });
                this.textInput.on("changeDate", () => this.updateInputs(moment(monthPicker.getDate())));
                this.textInput.data("monthPicker", monthPicker);
                break;
        }
        if (this.singleDatePicker) {
            this.updateSingleDatePickerInputWhenSameDayIsClicked(picker);
            this.textInput.on("change", () => this.textInput.val() === "" && this.fromInput.val(""));
        }
    }
    updateSingleDatePickerInputWhenSameDayIsClicked(picker) {
        picker.on("apply.daterangepicker", (_e, dateRangePicker) => {
            if (!this.fromInput.val())
                this.updateInputs(dateRangePicker.startDate.clone());
        });
    }
    cleanUp() {
        switch (this.pickerLevel) {
            case "days":
                const dayPicker = this.textInput.data("daterangepicker");
                dayPicker.remove();
                this.emit("removed", dayPicker.container[0]);
                break;
            case "months":
                const monthPicker = this.textInput.data("monthPicker");
                monthPicker.destroy();
                break;
        }
    }
    setValue(from, to) {
        this.updateInputs(from, to);
        switch (this.pickerLevel) {
            case "days":
                const dayPicker = this.textInput.data("daterangepicker");
                dayPicker.setStartDate(from.tz(moment.tz.guess()));
                to && dayPicker.setEndDate(to.tz(moment.tz.guess()));
                break;
            case "months":
                const monthPicker = this.textInput.data("monthPicker");
                monthPicker.setDate(from.tz(moment.tz.guess()).toDate());
                break;
        }
        this.updateTextInput();
    }
    updateInputs(from, to) {
        if (this.showTime) {
            this.fromInput.val(from.clone().tz("Europe/London").format(this.format));
            to && this.toInput.val(to.clone().tz("Europe/London").format(this.format));
        }
        else {
            this.fromInput.val(from.format(this.format));
            to && this.toInput.val(to.format(this.format));
        }
        this.updateTextInput();
        this.emit("change");
    }
    updateTextInput() {
        var _a, _b;
        const values = this.getValue();
        if (!values.from && !values.to) {
            this.textInput.val("");
            return;
        }
        this.textInput.val(`${(_a = values.from) === null || _a === void 0 ? void 0 : _a.format(this.displayFormat)}${this.singleDatePicker ? "" : " - "}${this.singleDatePicker ? "" : (_b = values.to) === null || _b === void 0 ? void 0 : _b.format(this.displayFormat)}`);
    }
    getValue() {
        const from = this.fromInput.val() || undefined;
        const to = this.toInput.val() || undefined;
        if (this.showTime) {
            return {
                from: this.convertToTimezone(from),
                to: this.convertToTimezone(to),
            };
        }
        return {
            from: from ? moment(from, this.format) : undefined,
            to: to ? moment(to, this.format) : undefined,
        };
    }
    getFormattedValue() {
        var _a, _b;
        const value = this.getValue();
        return {
            from: (_a = value.from) === null || _a === void 0 ? void 0 : _a.format(this.format),
            to: (_b = value.to) === null || _b === void 0 ? void 0 : _b.format(this.format),
        };
    }
    convertToTimezone(date) {
        if (date)
            return moment.tz(date, this.format, "Europe/London").tz(moment.tz.guess());
    }
}
class DatePickerFactory extends EventEmitter {
    initialise(container) {
        const pickerElements = $(container).find("[data-date-picker]");
        pickerElements.toArray().forEach((p) => {
            const pickerElement = $(p);
            if (!pickerElement.data("date-picker-model")) {
                const datePicker = new DatePicker(pickerElement);
                pickerElement.data("date-picker-model", datePicker);
                datePicker.on("rendered", (container) => this.emit("rendered", container));
                datePicker.on("removed", (container) => this.emit("removed", container));
            }
        });
    }
    cleanUp(container) {
        const pickers = $(container).find("[data-date-picker]");
        pickers.toArray().forEach((p) => {
            const datePickerModel = ($(p).data("date-picker-model"));
            datePickerModel && datePickerModel.cleanUp();
        });
    }
    setValue(pickerElement, from, to) {
        const datePickerModel = (pickerElement.data("date-picker-model"));
        datePickerModel && datePickerModel.setValue(from, to);
    }
    getModels(pickerElements) {
        return pickerElements
            .toArray()
            .map((p) => {
            const pickerElement = $(p);
            return pickerElement.data("date-picker-model");
        })
            .filter((m) => !!m);
    }
}
export const datePickerFactory = new DatePickerFactory();
modal.on("show", (modal) => datePickerFactory.initialise(modal));
modal.on("hide", (modal) => datePickerFactory.cleanUp(modal));
cloneableFactory.on("itemAdded", (row) => datePickerFactory.initialise(row));
cloneableFactory.on("itemRemoved", (row) => datePickerFactory.cleanUp(row));
$(() => datePickerFactory.initialise(document));
